import { useEffect, useMemo, useState } from "react";

import { getAttachments } from "api";
import FilePreview from "components/v2/FilePreview/FilePreview";
import { prettifyBytes } from "helpers/commonHelpers";
import errorToast from "helpers/errorToast";
import { extractAllFilesFromMessages } from "helpers/trackingHelpers";
import usePermissions from "hooks/usePermissions";
import { DeletebleFileType } from "./ActivityFilesTypes";
import DeleteTrackingFileDialog from "./DeleteTrackingFileDialog";
import { useGetDefaultFilesReturn } from "./useGetDefaultFiles";
import "./ActivityFiles.scss";

interface ActivityFilesProps {
  /**
   * The tracking updates data.
   */
  trackingUpdatesData: any;
  /**
   * The shipment ID.
   */
  shipmentId: string;
  /**
   * Whether the shipment is off-platform.
   */
  isOffPlatform: boolean;
  /**
   * The default files data.
   */
  defaultFilesData?: useGetDefaultFilesReturn;
  /**
   * The files extracted from messages.
   */
  filesExtractedFromMessages?: ReturnType<typeof extractAllFilesFromMessages>;
}

export default function ActivityFiles({
  trackingUpdatesData,
  shipmentId,
  isOffPlatform,
  defaultFilesData,
  filesExtractedFromMessages,
}: ActivityFilesProps) {
  const { permissions } = usePermissions();
  const { email, permissions: permissionData } = JSON.parse(permissions ?? "{}");
  const [bucketAttachments, setBucketAttachments] = useState<DeletebleFileType[]>([]);

  const {
    isLoadingShipmentDetails,
    isLoadingBol,
    handleClickShipmentDetails,
    handleClickBol,
    shipmentDetailsFileName,
    bolFileName,
  } = defaultFilesData ?? {};

  const [filesBeingDeletedByUpdateId, setFilesBeingDeletedByUpdateId] = useState<string[]>([]);
  const [selectedFile, setSelectedFile] = useState<DeletebleFileType | null>(null);
  const [openDeleteTrackingFileDialog, setOpenDeleteTrackingFileDialog] = useState(false);

  useEffect(() => {
    if (permissionData?.quoting?.view_quote_requests || permissionData?.tracking?.view_shipments) {
      getFileAttachments();
    }
  }, [permissionData?.quoting?.view_quote_requests, permissionData?.tracking?.view_shipments]);

  const updates: any[] = [...(trackingUpdatesData?.updates ?? [])].reverse();

  const uploadedFilesFromUpdates = useMemo(() => {
    if (!updates) return [];
    const filteredUpdates = updates.filter(
      (update) => (update.type === "upload" || update.type === "delivered") && !update.attachment_deleted,
    );
    const uploadedFiles: DeletebleFileType[] = [];

    filteredUpdates.forEach((update) => {
      const { tracking_key, author_email } = update;
      const updateData = update.data?.filter((item: any) =>
        ["attachment_url", "attachment_name", "attachment_size"].includes(item.key),
      );
      for (let i = 0; i < updateData.length; i += 3) {
        if (updateData?.[i].key !== "attachment_url") break;
        const parts = updateData[i + 1].value.split("/");
        const url = updateData[i].value;
        const name = parts[parts.length - 1];
        const size = updateData[i + 2].value ? prettifyBytes(updateData[i + 2].value) : "";
        const currentFile: DeletebleFileType = {
          url,
          name,
          size,
          canDelete: author_email === email,
          updateId: tracking_key,
        };
        uploadedFiles.push(currentFile);
      }
    });

    return uploadedFiles;
  }, [updates]);

  const filesFromMessages = filesExtractedFromMessages?.flatMap((item) =>
    item.links.map((linkObj) => ({
      url: linkObj.url,
      name: linkObj.name,
    })),
  );

  const getFileAttachments = () => {
    function formatBucketData(data: any): DeletebleFileType[] {
      return data.map((item: any) => ({
        id: item?.file?.id || item?.id,
        url: item?.file?.url || item?.url,
        name: item?.file?.name || item?.name,
      }));
    }

    getAttachments(shipmentId)
      .then((res) => setBucketAttachments(formatBucketData(res.data.bucket_attachments.concat(res.data.other_attachments))))
      .catch((err) => errorToast(err));
  };

  const handleClickDeleteFile = (file: DeletebleFileType) => {
    setSelectedFile(file);
    setOpenDeleteTrackingFileDialog(true);
  };

  return (
    <>
      {selectedFile && (
        <DeleteTrackingFileDialog
          isOpen={openDeleteTrackingFileDialog}
          close={() => setOpenDeleteTrackingFileDialog(false)}
          shipmentId={shipmentId}
          selectedFile={selectedFile}
          setFilesBeingDeletedByUpdateId={setFilesBeingDeletedByUpdateId}
        />
      )}
      <div className="activity-files">
        {!isOffPlatform && (
          <>
            <FilePreview
              fileName={shipmentDetailsFileName}
              isLoading={isLoadingShipmentDetails}
              onClick={handleClickShipmentDetails}
            />

            <FilePreview fileName={bolFileName} isLoading={isLoadingBol} onClick={handleClickBol} />
          </>
        )}

        {filesFromMessages?.map((file, index) => (
          <FilePreview key={index} fileName={file.name} url={file.url} />
        ))}

        {bucketAttachments.map((file, index) => (
          <FilePreview key={index} fileName={file.name} url={file.url} />
        ))}

        {uploadedFilesFromUpdates?.map((file, index) => (
          <FilePreview
            key={index}
            fileName={file.name}
            url={file.url}
            fileSize={file.size}
            onClose={file && file.canDelete ? () => handleClickDeleteFile(file) : undefined}
            isLoading={filesBeingDeletedByUpdateId.includes(file.updateId)}
          />
        ))}
      </div>
    </>
  );
}

ActivityFiles.defaultProps = {
  filesExtractedFromMessages: undefined,
  defaultFilesData: undefined,
};
